import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  LayoutDashboard, Users, FileText, Database, ClipboardList,
  LogOut, Menu, X, ChevronDown, Calendar, Upload, BarChart3,
  Activity, TrendingUp, MessageSquareText, AlertCircle, Boxes,
  BookOpen, Store, PackageCheck, KeySquare,
} from 'lucide-react';
import { Button } from './ui/button';
import { docsApi } from '../lib/api';
import { toast } from 'sonner';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from './ui/dropdown-menu';

const adminMenu = [
  { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/empresas', icon: Users, label: 'Empresas' },
  { path: '/campanas', icon: Calendar, label: 'Campañas' },
  { path: '/indicadores', icon: Boxes, label: 'Indicadores' },
  { path: '/estadisticas', icon: BarChart3, label: 'Estadísticas Sector' },
  { path: '/catalogo', icon: Store, label: 'Catálogo' },
  { path: '/admin/catalogo', icon: PackageCheck, label: 'Admin · Catálogo' },
  { path: '/agente-ia', icon: MessageSquareText, label: 'Agente IA' },
  { path: '/incidencias', icon: AlertCircle, label: 'Incidencias' },
  { path: '/auditoria', icon: ClipboardList, label: 'Auditoría' },
  { path: '/manual', icon: BookOpen, label: 'Manual · Versiones' },
];

const empresaMenu = [
  { path: '/dashboard', icon: LayoutDashboard, label: 'Mi Panel' },
  { path: '/subir-datos', icon: Upload, label: 'Subir Datos' },
  { path: '/mis-datos', icon: Database, label: 'Mis Datos' },
  { path: '/benchmark', icon: TrendingUp, label: 'Comparativa Sector' },
  { path: '/catalogo', icon: Store, label: 'Catálogo' },
  { path: '/mis-publicaciones', icon: PackageCheck, label: 'Mis publicaciones' },
  { path: '/mis-accesos', icon: KeySquare, label: 'Mis accesos' },
  { path: '/campanas', icon: Calendar, label: 'Campañas' },
  { path: '/agente-ia', icon: MessageSquareText, label: 'Agente IA' },
  { path: '/mi-contrato', icon: FileText, label: 'Mi Contrato' },
  { path: '/incidencias', icon: AlertCircle, label: 'Incidencias' },
  { path: '/manual', icon: BookOpen, label: 'Manual' },
];

const auditorMenu = [
  { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/empresas', icon: Users, label: 'Empresas' },
  { path: '/campanas', icon: Calendar, label: 'Campañas' },
  { path: '/estadisticas', icon: BarChart3, label: 'Estadísticas' },
  { path: '/catalogo', icon: Store, label: 'Catálogo' },
  { path: '/agente-ia', icon: MessageSquareText, label: 'Agente IA' },
  { path: '/incidencias', icon: AlertCircle, label: 'Incidencias' },
  { path: '/auditoria', icon: ClipboardList, label: 'Auditoría' },
  { path: '/manual', icon: BookOpen, label: 'Manual' },
];

export default function Layout({ children }) {
  const { user, logout, isAdmin, isEmpresa, isAuditor } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const menuItems = isAdmin ? adminMenu : (isAuditor ? auditorMenu : empresaMenu);
  const roleLabel = isAdmin ? 'Administrador AEA' : (isAuditor ? 'Auditor' : 'Empresa Asociada');

  const handleLogout = () => { logout(); navigate('/login'); };

  const downloadManual = async () => {
    try {
      const r = await docsApi.downloadManual();
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = 'Manual_Espacio_Datos_AEA.pdf';
      a.click(); window.URL.revokeObjectURL(url);
      toast.success('Manual descargado');
    } catch {
      toast.error('No se pudo descargar el manual');
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 flex">
      {/* Sidebar - Desktop */}
      <aside className="hidden lg:flex lg:flex-col lg:w-64 lg:fixed lg:inset-y-0 bg-aea-navy">
        <div className="flex-1 flex flex-col pt-6 pb-4 overflow-y-auto">
          <div className="flex items-center flex-shrink-0 px-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-lg aluminum-bg flex items-center justify-center shadow-md">
                <Activity className="w-6 h-6 text-aea-navy" strokeWidth={2.5} />
              </div>
              <div>
                <h1 className="text-white font-outfit font-bold text-lg leading-none">AEA</h1>
                <p className="text-slate-400 text-[11px] mt-1">Espacio de Datos</p>
              </div>
            </div>
          </div>

          <nav className="flex-1 px-3 space-y-1">
            {menuItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.path.replace('/', '')}`}
                  className={`group flex items-center px-3 py-2.5 text-sm font-medium rounded-md transition-all ${
                    isActive
                      ? 'bg-aea-blue text-white shadow-sm'
                      : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                  }`}
                >
                  <item.icon className={`mr-3 h-[18px] w-[18px] ${isActive ? 'text-white' : 'text-slate-400 group-hover:text-white'}`} strokeWidth={1.5} />
                  {item.label}
                </Link>
              );
            })}
          </nav>
        </div>

        <div className="flex-shrink-0 flex flex-col border-t border-slate-700/60 p-4 gap-3">
          <button
            onClick={downloadManual}
            data-testid="download-manual-btn"
            className="flex items-center gap-2 text-xs text-slate-300 hover:text-white transition-colors">
            <BookOpen className="h-4 w-4" strokeWidth={1.75} />
            Manual de uso (PDF)
          </button>
          <div className="flex items-center w-full">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate" data-testid="sidebar-user-name">{user?.name}</p>
              <p className="text-[11px] text-slate-400 truncate">{roleLabel}</p>
            </div>
            <Button
              variant="ghost" size="icon"
              onClick={handleLogout}
              data-testid="logout-button"
              className="text-slate-400 hover:text-white hover:bg-slate-800"
            >
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile sidebar */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-40">
          <div className="fixed inset-0 bg-slate-900/80" onClick={() => setSidebarOpen(false)} />
          <aside className="fixed inset-y-0 left-0 w-64 bg-aea-navy">
            <div className="flex items-center justify-between p-4 border-b border-slate-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg aluminum-bg flex items-center justify-center">
                  <Activity className="w-6 h-6 text-aea-navy" strokeWidth={2.5} />
                </div>
                <span className="text-white font-outfit font-bold">AEA</span>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(false)} className="text-white">
                <X className="h-5 w-5" />
              </Button>
            </div>
            <nav className="p-2 space-y-1">
              {menuItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    className={`group flex items-center px-3 py-2.5 text-sm font-medium rounded-md transition-all ${
                      isActive ? 'bg-aea-blue text-white' : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                    }`}
                  >
                    <item.icon className="mr-3 h-5 w-5" strokeWidth={1.5} />
                    {item.label}
                  </Link>
                );
              })}
            </nav>
          </aside>
        </div>
      )}

      {/* Main content */}
      <div className="lg:pl-64 flex flex-col flex-1">
        <header className="sticky top-0 z-10 bg-white/85 backdrop-blur-md border-b border-slate-200">
          <div className="flex items-center justify-between h-16 px-4 lg:px-8">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(true)} className="lg:hidden" data-testid="mobile-menu-button">
                <Menu className="h-5 w-5" />
              </Button>
              <h2 className="text-lg font-outfit font-semibold text-slate-800">
                {menuItems.find((item) => item.path === location.pathname)?.label || 'Panel'}
              </h2>
            </div>

            <div className="flex items-center gap-4">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-trigger">
                    <div className="w-8 h-8 rounded-full bg-aea-blue flex items-center justify-center text-white text-sm font-semibold">
                      {user?.name?.charAt(0).toUpperCase()}
                    </div>
                    <span className="hidden sm:inline text-sm font-medium text-slate-700">{user?.name}</span>
                    <ChevronDown className="h-4 w-4 text-slate-500" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium">{user?.name}</p>
                    <p className="text-xs text-slate-500">{user?.email}</p>
                    <p className="text-[10px] uppercase tracking-wider text-aea-blue mt-1 font-bold">{roleLabel}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={downloadManual} data-testid="dropdown-manual">
                    <BookOpen className="mr-2 h-4 w-4" />
                    Manual de uso (PDF)
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} data-testid="dropdown-logout">
                    <LogOut className="mr-2 h-4 w-4" />
                    Cerrar sesión
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        <main className="flex-1 p-4 lg:p-8">{children}</main>
      </div>
    </div>
  );
}
